import React from "react";
import { NavLink } from "react-router-dom";

import { useSidebarMenuContext } from "@/contexts/sidebar-menu-context";
import { cn } from "@/utils/cn";

import { Icon, Text } from "../ui";

export interface SidebarNavigationLinkProps {
  /** Alert count, if any. */
  count?: number;
  /** Optional max to cap count (ie: N+) */
  countMax?: number;
  /** URL to an SVG icon. */
  icon: string;
  activeIcon: string;
  /** Link label. */
  text: React.ReactNode;
  /** Route to an internal page. */
  to?: string;
  /** Callback when the link is clicked. */
  onClick?: React.EventHandler<React.MouseEvent>;
  /** Should show a dot indicator */
  showIndicator?: boolean;
  className?: string;
  ref?: React.Ref<HTMLAnchorElement | HTMLButtonElement>;
}

const baseClasses =
  "flex items-center w-full px-4 py-3 text-base font-semibold space-x-4 rtl:space-x-reverse rounded-full group text-gray-600 hover:text-gray-900 dark:text-gray-600 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-primary-900";

/** Desktop sidebar navigation link. */
const SidebarNavigationLink = (props: SidebarNavigationLinkProps) => {
  const {
    icon,
    activeIcon,
    text,
    to = "",
    count,
    countMax,
    onClick,
    showIndicator = false,
    className,
    ref,
  } = props;
  const { setIsSidebarOpen } = useSidebarMenuContext();

  const isCurrentPath = (): boolean => {
    if (!to) {
      return false;
    }

    const exactMatch = location.pathname === to;
    const beginningMatch = location.pathname.startsWith(to);
    return exactMatch || (to === "/" ? false : beginningMatch);
  };

  const isActive = isCurrentPath();

  const scrollToTop = () => {
    window.scrollTo({ top: 0 });
  };

  const handleClick: React.EventHandler<React.MouseEvent> = (e) => {
    if (isActive) scrollToTop();
    if (onClick) {
      onClick(e);
    }
    if (setIsSidebarOpen) {
      setIsSidebarOpen(false);
    }
  };

  const wrapperClass = cn(
    baseClasses,
    {
      "text-gray-900! dark:text-gray-100!": isActive,
    },
    className,
  );

  const contents = (
    <>
      <span className="relative">
        <Icon
          src={activeIcon}
          count={showIndicator || !isActive ? undefined : count}
          countMax={showIndicator || !isActive ? undefined : countMax}
          className={cn("size-5", {
            hidden: !isActive,
          })}
        />
        <Icon
          src={icon}
          count={showIndicator || isActive ? undefined : count}
          countMax={showIndicator || isActive ? undefined : countMax}
          className={cn("size-5", {
            hidden: isActive,
          })}
        />
        {showIndicator && (
          <div className="absolute -top-1 -right-1 size-2 rounded-full bg-accent-pink-300 ring-2 ring-white dark:ring-gray-900" />
        )}
      </span>
      <Text weight={isActive ? "semibold" : "medium"} theme="inherit">
        {text}
      </Text>
    </>
  );

  if (to) {
    return (
      <NavLink
        to={to}
        onClick={handleClick}
        className={wrapperClass}
        ref={ref as React.Ref<HTMLAnchorElement>}
      >
        {contents}
      </NavLink>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={wrapperClass}
      ref={ref as React.Ref<HTMLButtonElement>}
      data-testid="sidebar-navigation-link"
    >
      {contents}
    </button>
  );
};

export default SidebarNavigationLink;
